// src/components/MedicAppointmentsBase.js
import React, {Component} from "react";
import APIService from "../../modules/apiService";
import Helpers from "../../modules/helpers";
import DateTimeService from "../../modules/DateTimeService";
import AuthService from "../../modules/authService";
import ConfigService from "../../modules/configService";
import locales_es from "../../locales/es";
import {
  APPOINTMENT_PRESENTIAL_TYPE,
  APPOINTMENT_STATUS_FREE,
  APPOINTMENT_VIRTUAL_TYPE,
  DEFAULT_TIME_ZONE,
  HREF_PAGE_MEDIC,
  hrefDashboard,
  hrefLogin,
  HREF_REGISTER_PATIENT,
  APPOINTMENT_PAYMENT_STATUS_NOT_APPLY,
  USER_TYPE_PATIENT,
  USER_TYPE_MEDIC,
  CURRENCY_ID_ARS,
  CURRENCY_ID_USD
} from "../../models/constants";
import Spinner from "../spinner";
import Form from "../../components/form";
import AppointmentSuccess from "../appointmentSuccess";
import AppointmentScheduled from "../appointmentScheduled";
import PostAppoinmentPriceSelection from "../postAppoinmentPriceSelection";
import BankTransferData from "../bankTransferData";
import ExternalCheckoutData from "../externalCheckoutData";
import Loading from "../loading";
import moment from "moment-timezone";
import "./index.css";

export default class MedicAppointmentsBase extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      availableAppointments: null,
      selectedDate: null,
      selectedAppointment: null,
      firstDayAppointmentAvailable: null,
      nextDayAppointmentAvailable: null,
      patient: "me",
      owner_str: "",
      disableNextAppointment: false,
      clinics: null,           // sólo para “no branded”
      clinicId: null,          // sólo para “branded”
      clinicName: null,
      clinicAddress: null,
      clinicLat: null,
      clinicLng: null,
      clinicWebUrl: null,
      clinicVideocallBaseUrl: null,
      interruptedAgenda: false,
      notConfiguredAgenda: false,
      appointmentTypes: [],
      appointmentTypeId: null,
      timetables: [],
      prices: null,
      event: null,
      paymentMethods: null,
      bankTransferData: null,
      externalCheckoutData: null,
      interruptedAgendaOnlyPatients: false
    };

    this.api = new APIService();
    this.helpers = new Helpers();
    this.dateTimeService = new DateTimeService();
    this.auth = new AuthService();
    this.configService = new ConfigService();
  }

  componentDidMount() {
    // Si la propiedad interrupted_agenda viene en props.medic, detenemos todo
    if (this.props.medic && this.props.medic.interrupted_agenda) {
      this.setState({interruptedAgenda: true});
      return;
    }

    // Chequeamos si la agenda está “interrumpida sólo para pacientes”
    this.checkInterruptedAgendaOnlyPatients();
  }

  checkInterruptedAgendaOnlyPatients() {
    if (this.auth.isLoggedUser() && this.props.medic?.interrupted_agenda_only_patients) {
      this.api
        .getMyMedics()
        .then((res) => {
          const found = res.data.some((m) => m.id === this.props.medic.id);
          if (!found) {
            this.setState({interruptedAgendaOnlyPatients: true});
          } else {
            this.initClinicsOrLocal();
          }
        })
        .catch((err) => {
          this.props.showMainModal(locales_es.errorModal.title, this.helpers.getErrorMsg(err));
          this.initClinicsOrLocal();
        });
    } else {
      this.initClinicsOrLocal();
    }
  }

  initClinicsOrLocal() {
    // Si me piden “branded”, uso ConfigService para traer la clínica local
    if (this.props.useBranded) {
      this.configService
        .getLocalClinicData()
        .then((res) => {
          const {id, name, address, floor, lat, lng, web_url, videocall_base_url} = res;
          this.setState(
            {
              clinicId: id,
              clinicName: name,
              clinicAddress: address,
              clinicFloor: floor,
              clinicLat: lat,
              clinicLng: lng,
              clinicWebUrl: web_url,
              clinicVideocallBaseUrl: videocall_base_url
            },
            () => {
              this.loadCommonData(); // siguiente paso
            }
          );
        })
        .catch((err) => {
          console.error(err);
        });
    } else {
      // Versión “sin branded” → traigo todas las clínicas del médico
      this.api
        .getClinicsByUser(this.props.medic.id)
        .then((res) => {
          this.setState({clinics: res.data}, () => {
            this.loadCommonData();
          });
        })
        .catch((err) => {
          this.props.showMainModal(locales_es.errorModal.title, this.helpers.getErrorMsg(err));
        });
    }
  }

  loadCommonData() {
    // Ahora que tengo el/los datos de clínica en el state, pido horarios y tipos
    this.getTimetables();
    this.getAppointmentTypesAndContinue();
  }

  getTimetables() {
    const obj = {
      medic_id: this.props.medic.id,
      ...(this.props.useBranded ? {clinic_id: this.state.clinicId} : {})
    };
    this.api
      .getTimetables(obj)
      .then((res) => this.setState({timetables: res.data}))
      .catch((err) => {
        this.props.showMainModal(locales_es.errorModal.title, this.helpers.getErrorMsg(err));
      });
  }

  getAppointmentTypesAndContinue() {
    this.api
      .getMedicAppointmentsTypes(this.props.medic.id)
      .then((res) => {
        this.setState(
          {
            appointmentTypes: res.data,
            appointmentTypeId: res.data?.length ? res.data[0].id : null
          },
          () => {
            this.attemptInitialAppointmentSearchWithRetries();
          }
        );
      })
      .catch((err) => {
        this.props.showMainModal(locales_es.errorModal.title, this.helpers.getErrorMsg(err));
      });
  }

  attemptInitialAppointmentSearchWithRetries(retryOffsets = [0, 30, 60], attempt = 0) {
    const offsetDays = retryOffsets[attempt];
    const date = new Date();
    date.setDate(date.getDate() + offsetDays);

    this.api
      .getNextAppointment({
        medic_id: this.props.medic.id,
        status: APPOINTMENT_STATUS_FREE,
        type_id: this.state.appointmentTypeId,
        ...(this.props.useBranded ? {clinic_id: this.state.clinicId} : {}),
        start: date.toISOString()
      }, true)
      .then((res) => {
        if (res.data) {
          const nextAppointmentDate = res.data.start;
          this.setState(
            {
              firstDayAppointmentAvailable: nextAppointmentDate,
              nextDayAppointmentAvailable: nextAppointmentDate,
              selectedDate: nextAppointmentDate
            },
            () => this.getAvailableAppointments()
          );
        } else if (attempt < retryOffsets.length - 1) {
          this.attemptInitialAppointmentSearchWithRetries(retryOffsets, attempt + 1);
        } else {
          this.setState({
            selectedDate: new Date().toISOString(),
            availableAppointments: [],
            notConfiguredAgenda: true
          });
        }
      })
      .catch((err) => {
        if (attempt < retryOffsets.length - 1) {
          this.attemptInitialAppointmentSearchWithRetries(retryOffsets, attempt + 1);
        } else {
          this.setState({
            selectedDate: new Date().toISOString(),
            availableAppointments: [],
            notConfiguredAgenda: true
          });
          console.log("Falló al buscar turnos disponibles incluso tras reintentos:", err);
        }
      });
  }


  setAvailableAppointmentsLoading() {
    this.setState({availableAppointments: null});
  }

  getNextAvailableAppointment(retry) {
    this.setAvailableAppointmentsLoading();
    const objData = {
      medic_id: this.props.medic.id,
      status: APPOINTMENT_STATUS_FREE,
      type_id: this.state.appointmentTypeId,
      ...(this.props.useBranded ? {clinic_id: this.state.clinicId} : {})
    };
    if (retry) {
      objData.start = new Date(this.state.selectedDate).toISOString();
    }

    this.api
      .getNextAppointment(objData, true)
      .then((res) => {
        if (res.data) {
          const nextAppointmentDate = res.data.start;
          if (!this.state.firstDayAppointmentAvailable) {
            this.setState({firstDayAppointmentAvailable: nextAppointmentDate});
          }
          this.setState(
            {
              nextDayAppointmentAvailable: nextAppointmentDate,
              selectedDate: nextAppointmentDate
            },
            () => this.getAvailableAppointments()
          );
        }
      })
      .catch((err) => {
        this.setState({
          selectedDate: new Date().toISOString(),
          availableAppointments: [],
          notConfiguredAgenda: true
        });
        console.log(err);
      });
  }

  getAvailableAppointments(retry = false) {
    this.setAvailableAppointmentsLoading();
    const objData = {
      medic_id: this.props.medic.id,
      status: APPOINTMENT_STATUS_FREE,
      type_id: this.state.appointmentTypeId,
      start: this.dateTimeService
        .getDateStartOfDay(
          this.dateTimeService.convertTZ(this.state.selectedDate, DEFAULT_TIME_ZONE),
          false
        )
        .toISOString(),
      end: this.dateTimeService
        .getDateEndOfDay(
          this.dateTimeService.convertTZ(this.state.selectedDate, DEFAULT_TIME_ZONE),
          false
        )
        .toISOString(),
      ...(this.props.useBranded ? {clinic_id: this.state.clinicId} : {})
    };

    this.api
      .getAppointments(objData, true)
      .then((res) => {
        this.setState({availableAppointments: res.data}, () => {
          if (retry && !this.state.availableAppointments.length) {
            this.getNextAvailableAppointment(true);
          } else if (!this.props.useBranded && this.state.availableAppointments.length) {
            // Solo “no branded”: elijo la clínica filtrando por el primer appointment
            const clinicSelected = this.state.clinics.filter(
              (c) => Number(c.id) === Number(this.state.availableAppointments[0].clinic_id)
            );
            if (clinicSelected.length) {
              this.setClinicData(clinicSelected[0]);
            }
          }
          this.setDisableNextAppointment(false);
        });
      })
      .catch((err) => {
        this.props.showMainModal(locales_es.infoModal.title, this.helpers.getErrorMsg(err));
        console.log(err);
      });
  }

  setClinicData(clinic) {
    this.setState({
      clinicName: clinic.name,
      clinicAddress: clinic.address,
      clinicFloor: clinic.floor,
      clinicLat: clinic.lat,
      clinicLng: clinic.lng,
      clinicWebUrl: clinic.web_url,
      clinicVideocallBaseUrl: clinic.videocall_base_url
    });
  }

  setDisableNextAppointment(bool) {
    this.setState({disableNextAppointment: bool});
  }

  changeDateAppointment(days) {
    this.cleanSelectedAppointment();
    if (days < 0 && this.isDisablePreviousDate) return;

    const beforeDate = new Date(this.state.nextDayAppointmentAvailable);
    const currentDate = new Date(this.state.selectedDate);
    currentDate.setDate(currentDate.getDate() + days);

    this.setState({selectedDate: currentDate.toISOString()}, () => {
      const isAfter = beforeDate.getTime() < currentDate.getTime();
      this.getAvailableAppointments(isAfter);
    });
  }

  get isDisablePreviousDate() {
    const sel = new Date(this.state.selectedDate).getTime();
    const first = new Date(this.state.firstDayAppointmentAvailable).getTime();
    return (
      !this.state.selectedDate ||
      sel <= first ||
      sel < new Date().getTime()
    );
  }

  handleSelectAppointment(appointment) {
    this.setState({selectedAppointment: appointment});
  }

  cleanSelectedAppointment() {
    this.setState({selectedAppointment: null});
  }

  onValueChange = (ev) => {
    this.setState({owner_str: "", patient: ev.target.value});
  };

  handleChange = (ev) => {
    this.setState({owner_str: ev.target.value});
  };

  handleFormChange = (field) => (ev) => {
    this.setState({[field]: ev.target.value, notConfiguredAgenda: false}, () => {
      this.getNextAvailableAppointment();
    });
  };

  goToAppointmentReview = () => {
    if (!this.auth.isLoggedUser()) {
      this.props.showMainModal(
        locales_es.infoModal.title,
        locales_es.youNeedToBeLoggedInToRequestAnAppointment
      );
      return;
    }
    if (this.auth.getLocalUserType() !== USER_TYPE_PATIENT) {
      this.props.showMainModal(
        locales_es.infoModal.title,
        locales_es.youNeedToBeLoggedInAsAPatientToRequestAnAppointment
      );
      return;
    }
    this.goToPricesReview();
  };

  goToPricesReview = () => {
    if (!this.state.selectedAppointment) {
      this.props.showMainModal(
        locales_es.errorModal.title,
        locales_es.selectAnAppointment
      );
      return;
    }
    const appointmentTimetableId = this.state.selectedAppointment?.timetable_id;
    if (!appointmentTimetableId) {
      this.props.showMainModal(
        locales_es.infoModal.title,
        locales_es.chooseAScheduleAtLeast
      );
      return;
    }
    const precios =
      this.state.timetables.find(
        (tt) => Number(tt.id) === Number(appointmentTimetableId)
      )?.prices || [];

    if (precios.length > 1) {
      this.setState({prices: precios});
    } else if (precios.length === 1) {
      // Number(precios[0].value) ? this.postAppointment(precios[0].id) : this.setState({ prices: precios });
      this.setState({prices: precios});
    } else {
      // “A Consultar”
      this.postAppointment();
    }
  };

  postAppointment = (priceId) => {
    const objData = {...this.state.selectedAppointment};
    objData.timetable_slot_id = objData.id;
    objData.type_id = this.state.appointmentTypeId;
    if (this.state.owner_str) objData.owner_str = this.state.owner_str;
    if (priceId) objData.timetable_price_id = priceId;

    this.setSendButtonDisable(true);
    this.api
      .postAppointment(objData)
      .then((res) => {
        if (
          res.data.payment_status !== APPOINTMENT_PAYMENT_STATUS_NOT_APPLY &&
          res.data.consultation_price
        ) {
          this.loadPaymentInfo(res);
        } else {
          this.loadEvent(res);
        }
      })
      .catch((err) => {
        this.props.showMainModal(
          locales_es.errorModal.title,
          this.helpers.getErrorMsg(err)
        );
        this.setSendButtonDisable(false);
      });
  };

  loadEvent = (res) => {
    const appointment = {...this.state.selectedAppointment};
    const startDatetime = moment(new Date(appointment.start));
    const endDatetime = moment(new Date(appointment.end));
    const duration = moment
      .duration(endDatetime.diff(startDatetime))
      .asHours();

    const location =
      Number(this.state.appointmentTypeId) === Number(APPOINTMENT_VIRTUAL_TYPE)
        ? `${this.state.clinicVideocallBaseUrl}${res.data.videocall_hash}`
        : Number(this.state.appointmentTypeId) ===
        Number(APPOINTMENT_PRESENTIAL_TYPE)
          ? this.state.clinicLat && this.state.clinicLng
            ? `https://www.google.com/maps/search/?api=1&query=${this.state.clinicLat},${this.state.clinicLng}`
            : `https://google.com/maps/place/${this.state.clinicAddress}`
          : "";

    this.setState(
      {
        event: {
          description: `Turno con ${this.props.medic.name} ${this.props.medic.lastname}`,
          duration,
          endDatetime: endDatetime.format("YYYYMMDDTHHmmssZ"),
          location,
          startDatetime: startDatetime.format("YYYYMMDDTHHmmssZ"),
          title: locales_es.medicalAppointment,
          timezone: appointment.time_zone,
          payment_url: res.data.payment_url,
          before_payment_type: res.data.before_payment_type,
          before_payment_amount: res.data.before_payment_amount,
          payment_status: res.data.payment_status,
          consultation_price: res.data.consultation_price,
          init_point_before_payment_paypal:
          res.data.init_point_before_payment_paypal,
          init_point_before_payment: res.data.init_point_before_payment,
          requires_payment: res.data.requires_payment,
          created_at: res.data.created_at,
        }
      },
      () => this.setSendButtonDisable(false)
    );
  };

  loadPaymentInfo = (response) => {
    if (response) {
      this.api
        .getPaymentMethodsByAppointmentId(response.data.id)
        .then((res) => {
          this.setState({paymentMethods: res.data}, () => {
            this.loadEvent(response);
          });
        })
        .catch((err) => {
          this.props.showMainModal(
            locales_es.errorModal.title,
            this.helpers.getErrorMsg(err)
          );
          this.loadEvent(response);
        });
    } else {
      this.setState({paymentMethods: false});
      this.loadEvent(response);
    }
  };

  acceptAction = () => {
    const objData = {...this.state.selectedAppointment};
    objData.timetable_slot_id = objData.id;
    objData.type_id = this.state.appointmentTypeId;
    if (this.state.owner_str) objData.owner_str = this.state.owner_str;

    const timetables = JSON.parse(JSON.stringify(this.state.timetables));
    const selectedTimetable = timetables.find(
      (tt) => tt.id === objData.timetable_id
    );

    const regex = /\n/gm;
    const customStyles = {
      width: "90%",
      height: "70%",
      overflow: "scroll",
      maxWidth: "initial"
    };

    const message =
      selectedTimetable?.footer_email_text?.replace(regex, "<br>") || null;
    if (message) {
      this.props.showMainModal(
        locales_es.infoModal.title,
        message,
        message.length > 300 || regex.test(selectedTimetable.footer_email_text)
          ? customStyles
          : null
      );
    }
    this.props.history.push(hrefDashboard);
  };

  setSendButtonDisable = (bool) => {
    this.setState({sendButtonDisable: bool});
  };

  onPriceSelection = (priceId) => {
    this.setState({prices: null}, () => {
      this.postAppointment(priceId);
    });
  };

  renderSelectedDate() {
    return (
      <>
        <span
          dangerouslySetInnerHTML={{
            __html: this.dateTimeService.parseEventDate(
              this.state.selectedDate,
              false
            )
          }}
        />
      </>
    );
  }

  render() {
    const {
      loading,
      availableAppointments,
      selectedDate,
      disableNextAppointment,
      clinicName,
      clinicAddress,
      clinicFloor,
      clinicLat,
      clinicLng,
      clinicWebUrl,
      interruptedAgenda,
      notConfiguredAgenda,
      appointmentTypeId,
      event,
      paymentMethods,
      bankTransferData,
      externalCheckoutData,
      sendButtonDisable,
      prices,
      interruptedAgendaOnlyPatients
    } = this.state;
    const {medic, useBranded} = this.props;

    // Inputs para el select de tipo de turno
    const inputs = [
      {
        label: locales_es.appointmentType,
        placeholder: locales_es.appointmentType,
        id: 1,
        state: "appointmentTypeId",
        value: this.state.appointmentTypeId,
        type: "select",
        required: true,
        options: this.state.appointmentTypes,
        wrapperCustomClassName: "form-group col-md-6"
      }
    ];

    // Handlers de pago, idénticos en ambas versiones
    const onBankTransferClick = (currencyId) => {
      this.setState({bankTransferData: true}, () => {
        if (currencyId === CURRENCY_ID_ARS) {
          this.api
            .getBankTransfer(this.props.medic.id)
            .then((res) => this.setState({bankTransferData: res.data}))
            .catch((err) =>
              this.props.showMainModal(
                locales_es.errorModal.title,
                this.helpers.getErrorMsg(err)
              )
            );
        }
        if (currencyId === CURRENCY_ID_USD) {
          this.api
            .getBankTransferUSD(this.props.medic.id)
            .then((res) => this.setState({bankTransferData: res.data}))
            .catch((err) =>
              this.props.showMainModal(
                locales_es.errorModal.title,
                this.helpers.getErrorMsg(err)
              )
            );
        }
      });
    };

    const onBankTransferClickCancel = () => {
      this.setState({bankTransferData: false});
    };

    const onExternalCheckoutClick = (selectedGateway) => {
      this.setState({externalCheckoutData: selectedGateway});
    };

    const onExternalCheckoutClickCancel = () => {
      this.setState({externalCheckoutData: false});
    };

    return interruptedAgenda ? (
      <div className="kt-section mt-3">
        <div className="kt-section__desc">
          <div className="alert alert-secondary" role="alert">
            <div className="alert-icon">
              <i className="flaticon2-information"/>
            </div>
            <div className="alert-text">
              {this.props.medic.interrupted_agenda ||
                locales_es.interruptedAgendaProfileMedicDefaultText}
            </div>
          </div>
        </div>
      </div>
    ) : interruptedAgendaOnlyPatients ? (
      <div className="kt-section mt-3">
        <div className="kt-section__desc">
          <div className="alert alert-warning" role="alert">
            <div className="alert-icon">
              <i className="flaticon-lock"/>
            </div>
            <div className="alert-text">
              {locales_es.interruptedAgendaProfileMedicDefaultText}.{" "}
              {this.props.medic.interrupted_agenda_only_patients_message || ""}
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div className="kt-section mt-3">
        {loading && <Loading/>}
        <div className="kt-section__desc">
          {this.state.appointmentTypes.length > 0 && (
            <Form styles="kt-form" inputs={inputs} handleChange={this.handleFormChange}/>
          )}
        </div>

        {notConfiguredAgenda ? null : appointmentTypeId === null ? (
          <div className="kt-section__desc">
            <Spinner/>
          </div>
        ) : Number(appointmentTypeId) === Number(APPOINTMENT_VIRTUAL_TYPE) ? (
          <>
            <div className="kt-section__desc">
              <strong>
                {locales_es.virtualAppointment} - {locales_es.availableAppointments}
              </strong>
            </div>
            <div className="kt-section__desc">
              <i className="flaticon-information"/> {locales_es.virtualAppointmentDescription}
            </div>
          </>
        ) : (
          <>
            <div className="kt-section__desc">
              <strong>
                {locales_es.consultingAppointment} - {locales_es.availableAppointments}
              </strong>
            </div>
            <div className="kt-section__desc">
              {locales_es.attentionAddress}:&nbsp;
              <a href={clinicWebUrl} target="_blank" rel="noreferrer">
                {clinicName}
              </a>
            </div>
            <div className="kt-demo-icon">
              <div className="kt-demo-icon__preview">
                <i className="fa fa-map-marker-alt"/>
              </div>
              <div
                className={`kt-demo-icon__class ${
                  useBranded ? "kt-demo-icon__class--branded" : ""
                }`}
              >
                <a
                  href={
                    clinicLat && clinicLng
                      ? `https://www.google.com/maps/search/?api=1&query=${clinicLat},${clinicLng}`
                      : `https://google.com/maps/place/${clinicAddress}`
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {clinicAddress} {clinicFloor ? `(${clinicFloor})` : ""}
                </a>
              </div>
            </div>
          </>
        )}

        {notConfiguredAgenda ? (
          <div className="kt-section__content">
            <div className="row medicloud-calendar-week mt-15">
              <div className="col text-center">
                <div className="alert alert-warning fade show" role="alert">
                  <div className="alert-icon">
                    <i className="flaticon-warning"/>
                  </div>
                  <div className="alert-text">
                    {locales_es.medicAgendaNotAvailable}
                    {this.state.appointmentTypes.length
                      ? `. ${locales_es.medicAppointmentTypeNotAvailable} ${
                        this.state.appointmentTypes.find(
                          (app) =>
                            Number(app.id) === Number(this.state.appointmentTypeId)
                        )?.name
                      }`
                      : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="kt-section__content">
            {/* Controles para cambiar de día */}
            <div className="row medicloud-calendar-week mt-15">
              <div
                onClick={() => this.changeDateAppointment(-1)}
                className={`col-3 ${
                  this.isDisablePreviousDate ? "invisible" : "visible"
                }`}
              >
                <i className="fa fa-chevron-circle-left"/>
              </div>
              <div className="col-6 text-center">
                <h6>
                  <span>{selectedDate && this.renderSelectedDate()}</span>
                </h6>
              </div>
              <div
                className={
                  disableNextAppointment
                    ? "col-3 text-right disabled"
                    : "col-3 text-right"
                }
                onClick={() => {
                  if (!disableNextAppointment) {
                    this.setState({disableNextAppointment: true}, () => {
                      this.changeDateAppointment(1);
                    });
                  }
                }}
              >
                <i className="fa fa-chevron-circle-right"/>
              </div>
            </div>

            {/* Lista de franjas disponibles */}
            <div className="row justify-content-center">
              <div className="col-xs-12">
                <div className="medicloud-calendar">
                  <div className="slots justify-content-center">
                    {availableAppointments === null ? (
                      <Spinner/>
                    ) : availableAppointments.length > 0 ? (
                      availableAppointments.map((appointment) => (
                        <div
                          key={appointment.id}
                          className={`slot ${
                            appointment === this.state.selectedAppointment ? " selected" : ""
                          }`}
                          onClick={() => this.handleSelectAppointment(appointment)}
                        >
                          <span>
                            {this.dateTimeService.parseEventTime(
                              appointment.start,
                              "full-string"
                            )}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center">
                        {locales_es.thereIsNoAvailableAppointmentsForTheSelectedDate}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Selección de paciente */}
            <div className="kt-separator kt-separator--border-dashed kt-separator--space-lg"/>

            <div className="form-group row">
              <label className="col-3 col-form-label">
                {locales_es.whoIsTheAppointmentPatient}
              </label>
              <div className="col-9">
                <div className="kt-radio-list">
                  <label className="kt-radio">
                    <input
                      type="radio"
                      name="radio3"
                      value="me"
                      onChange={this.onValueChange}
                      checked={this.state.patient === "me"}
                    />{" "}
                    {locales_es.theAppointmentPatientIsMe}
                    <span/>
                  </label>
                  <label className="kt-radio">
                    <input
                      type="radio"
                      name="radio3"
                      value="someone"
                      onChange={this.onValueChange}
                      checked={this.state.patient === "someone"}
                    />{" "}
                    {locales_es.theAppointmentPatientIsSomeoneelse}
                    <span/>
                  </label>
                  {this.state.patient === "someone" && (
                    <input
                      className="form-control"
                      type="text"
                      value={this.state.owner_str}
                      onChange={this.handleChange}
                      placeholder={locales_es.patientFullName}
                      disabled={this.state.patient === "me"}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Si no está logueado */}
            {!this.auth.isLoggedUser() && (
              <div className="alert alert-warning fade show" role="alert">
                <div className="alert-icon">
                  <i className="flaticon-warning"/>
                </div>
                <div className="alert-text">
                  {locales_es.youNeedToBeLoggedInToRequestAnAppointment}.&nbsp;
                  <a
                    href={`${HREF_REGISTER_PATIENT}?redirect=${
                      HREF_PAGE_MEDIC
                    }/${this.props.medic.id}`}
                  >
                    {locales_es.registerYourself}
                  </a>
                  &nbsp;{locales_es.or}&nbsp;
                  <a
                    href={`${hrefLogin}?redirect=${HREF_PAGE_MEDIC}/${this.props.medic.id}`}
                  >
                    {locales_es.loginYourself}
                  </a>
                </div>
              </div>
            )}

            {/* Botón de “Solicitar turno” */}
            <div className="text-right p-3 pt-0">
              <button
                onClick={this.goToAppointmentReview}
                type="button"
                className="btn btn-outline-brand btn-bold btn-font-sm btn-upper"
                disabled={sendButtonDisable}
              >
                {sendButtonDisable ? (
                  <div className="spinner-border text-success" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                ) : (
                  locales_es.requestAppointment
                )}
              </button>
            </div>

            {/* Mensaje de feedback si el médico está viendo su propia vista */}
            {this.auth.isLoggedUser() &&
              this.auth.getLocalUserType() === USER_TYPE_MEDIC &&
              medic.id === this.auth.getUserData().user.id && (
                <div className="alert alert-danger" role="alert">
                  <div className="alert-icon">
                    <i className="flaticon-warning"/>
                  </div>
                  <div className="alert-text">
                    {useBranded
                      ? "Estás viendo la vista que verán los pacientes al sacar los turnos"
                      : "Estás viendo la vista que verán los pacientes al sacar los turnos, y la que debes compartir con ellos para que saquen turnos contigo."}
                  </div>
                </div>
              )}
          </div>
        )}

        {/* Sección de confirmación/ pago / éxito */}
        {event && !bankTransferData && !externalCheckoutData ? (
          paymentMethods &&
          paymentMethods.length &&
          event.payment_status !== APPOINTMENT_PAYMENT_STATUS_NOT_APPLY ? (
            <AppointmentScheduled
              selectedAppointment={event}
              paymentMethods={paymentMethods}
              onContinue={this.acceptAction}
              medic={this.props.medic}
              showMainModal={this.props.showMainModal}
              onBankTransferClick={onBankTransferClick}
              onExternalCheckoutClick={onExternalCheckoutClick}
            />
          ) : (
            <AppointmentSuccess acceptAction={this.acceptAction} event={event}/>
          )
        ) : null}

        {/* Selección de precios si más de uno */}
        {prices && (
          <PostAppoinmentPriceSelection
            onCancel={() => this.setState({prices: null})}
            onPress={this.onPriceSelection}
            prices={prices}
          />
        )}

        {/* Si se abrió la ventana de transferencia bancaria */}
        {bankTransferData && (
          <BankTransferData
            bankTransferData={bankTransferData}
            selectedAppointment={event}
            onClickAcceptAction={this.acceptAction}
            onClickCancel={onBankTransferClickCancel}
          />
        )}

        {/* Si se abrió la ventana de checkout externo */}
        {externalCheckoutData && (
          <ExternalCheckoutData
            selectedGateway={externalCheckoutData}
            selectedAppointment={event}
            showMainModal={this.props.showMainModal}
            onClickCancel={onExternalCheckoutClickCancel}
          />
        )}
      </div>
    );
  }
}
